import { SoftSkillCategory } from 'generated/graphql';
import {
  isCategorySkill,
  TMissingCategorySkillsInRoles,
  TSkillsByCategory,
} from 'helpers/softSkills';
import { kebabCase } from 'lodash';
import { TSkillOverview } from 'views/Project/components/ProjectJobRolesOverviewSkillsList/ProjectJobRolesOverviewSkillsList.types';
import SkillsCategoryInlineNotification from 'views/Project/components/SkillsCategoryOverview/SkillsCategoryInlineNotification';
import { Tag, TagColor } from '@spotted-zebra-uk/ui-components';
import styles from './SkillsCategoryOverview.module.scss';

const categoryMap = [
  {
    title: 'Right for us',
    key: 'rightForUsSkills',
    category: SoftSkillCategory.RightForUs,
    label: 'values',
  },
  {
    title: 'Right for the future',
    key: 'rightForFuture',
    category: SoftSkillCategory.RightForTheFuture,
    label: 'indicators of potential',
  },
  {
    title: 'Cognitive abilities',
    key: 'cognitiveSummarySkills',
    category: SoftSkillCategory.CognitiveSummary,
    label: 'v2 cognitive abilities',
  },
];

const isCognitiveSkill = (skill: TSkillOverview) => {
  return skill.name.toLowerCase().includes('cognitive');
};

const getSkillColor = (
  skill: TSkillOverview,
  category?: string,
  isHighlighted?: boolean,
) => {
  if (isHighlighted) {
    return TagColor.INTERACTIVE;
  }
  if (
    isCognitiveSkill(skill) &&
    category === SoftSkillCategory.RightForTheRole
  ) {
    return TagColor.GREEN;
  }
  return isCategorySkill(skill) ? TagColor.BLUE : TagColor.DISABLED;
};

const SkillsCategoryOverview = ({
  softSkills,
  technicalSkills,
  missingCategorySkillsInRoles,
  highlightedSoftSkillIds,
  highlightedTechnicalSkillIds,
}: {
  softSkills: TSkillsByCategory;
  technicalSkills: TSkillOverview[];
  missingCategorySkillsInRoles: TMissingCategorySkillsInRoles;
  highlightedSoftSkillIds?: Set<number>;
  highlightedTechnicalSkillIds?: Set<number>;
}) => {
  return (
    <section className={styles.container}>
      <div className={styles.rightForRoleContainer}>
        <SkillsCategoryOverviewItem
          title="Right for the role"
          subtitle="Soft Skills"
          skills={softSkills.rightForRoleSkills}
          category={SoftSkillCategory.RightForTheRole}
          label="soft skills"
          highlightedSkillIds={highlightedSoftSkillIds}
        />
        <SkillsCategoryOverviewItem
          subtitle="Technical Skills"
          skills={technicalSkills}
          label="technical skills"
          highlightedSkillIds={highlightedTechnicalSkillIds}
        />
      </div>

      {categoryMap.map((category) => (
        <SkillsCategoryOverviewItem
          key={category.key}
          skills={
            softSkills[
              category.key as keyof Omit<
                typeof softSkills,
                'missingCategorySkills'
              >
            ]
          }
          title={category.title}
          label={category.label}
          highlightedSkillIds={highlightedSoftSkillIds}
        />
      ))}

      {/* 
An InlineNotification for each role that has missing category skills
*/}
      {missingCategorySkillsInRoles.map((role, index) => {
        const { name, missing } = role;
        return (
          <SkillsCategoryInlineNotification
            key={index}
            roleName={missingCategorySkillsInRoles.length === 1 ? '' : name}
            missingCategorySkills={missing}
          />
        );
      })}
    </section>
  );
};

export default SkillsCategoryOverview;

export const SkillsCategoryOverviewItem = ({
  skills,
  title,
  subtitle,
  category,
  label,
  highlightedSkillIds,
}: {
  skills: TSkillOverview[];
  title?: string;
  subtitle?: string;
  category?: SoftSkillCategory;
  label?: string;
  highlightedSkillIds?: Set<number>;
}) => {
  return (
    <div className={styles.categoryContainer}>
      {title && <div className={styles.title}>{title}</div>}
      {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
      <div className={styles.skillsContainer}>
        {skills.length === 0 ? (
          <p className={styles.noSkills}>
            {`The added Role Skills Profile(s) don't include any ${label}.`}
          </p>
        ) : (
          skills.map((skill) => {
            const isHighlighted = highlightedSkillIds?.has(skill.id) ?? false;
            return (
              <div
                data-testid={`project-job-roles-overview-${kebabCase(
                  label,
                )}-list-item`}
                key={skill.id}
              >
                <Tag
                  isPresentational
                  isDismissible={false}
                  tagColor={getSkillColor(skill, category, isHighlighted)}
                >
                  <span data-testid="project-job-roles-overview-skill-name">
                    {skill.name}
                  </span>
                </Tag>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
